<script lang="ts" setup>
import {cx} from 'class-variance-authority'
import {useManagedProjects} from '~/composables/cms/managed-projects'
import {lexicalToPlaintext} from '~/utils/lexical'
import {resolveRelations} from '~/utils/relations'

import Firms from '~/assets/firms.svg?component'
import ReviewsSmall from '~/components/workshop/reviews-small.vue'
import CheckGreen from '~/assets/icons/check-green.svg?component'
import Menu from '~/components/workshop/menu.vue'
import ShadowBorder from '~/components/managed-projects/shadow-border.vue'
import BlogSection from '~/components/workshop/blog-section.vue'
import OtherSection from '~/components/workshop/other-services.vue'
import SingleTestimonial from '~/components/managed-projects/single-testimonial.vue'
import BenefitCard from '~/components/managed-projects/benefit-card.vue'
import HowWeWorkCard from '~/components/managed-projects/how-we-work-card.vue'

const pixelGridLg = [
  '  111111111111111111111111111111111111111111111   ',
  ' 1111111111111111     111111211111111111111111    ',
  '  112221111111122111111111221211111111111111111   ',
  '       211111111111111111111111111111    2111111  ',
  '       211111111111111111111111111111    2111111  ',
  '                                    1    2111111  ',
  '       2111111                     11    2111111  ',
  '1111                     111111122111 221111111   ',
  '1111111111                  111111122111 221111111',
  '1111111111                                        ',
  '                            111111122111 221111111',
  '                            111111122111 221111111',
  '                                  122111 221111111',
  '                                      11 221111111',
].join('\n')

const benefitsPixelGrid = [
  '               111111               ',
  '            111111111111            ',
  '          111112221111111           ',
  '         11111111111111111          ',
  '           122111111221             ',
  '              111111                ',
].join('\n')

const lp = useLocalePath()

const {
  public: {baseUrl},
} = useRuntimeConfig()

const {fetch} = useManagedProjects()
const {data: initialData} = await useAsyncData('managed-projects', () => fetch())
const {data: managedProjects} = useLivePreview({initialData: initialData.value, depth: 4})

if (!managedProjects.value) {
  throw createError({status: 503, message: 'Could not load the page.'})
}

useSchemaOrg([
  {
    '@type': 'FAQPage',
    mainEntity: computed(
      () =>
        resolveRelations(managedProjects.value?.faq?.questions).map(item => ({
          '@type': 'Question',
          name: item.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: lexicalToPlaintext(item.body),
          },
        })) || []
    ),
  },
])

const currentTab = ref<string>('Startup')

function onTabChange(option: string) {
  currentTab.value = option
}

const seo = managedProjects.value?.meta

useSeoMeta({
  title: seo?.title,
  description: seo?.description,
  ogUrl: `${baseUrl}/managed-projects`,
  ogTitle: seo?.ogTitle || seo?.title,
  ogDescription: seo?.ogDescription || seo?.description,
  ogImage: resolveMediaUrl(seo?.image),
  ogImageAlt: resolveMediaAlt(seo?.image),
})

const gradient = 'text-transparent bg-clip-text bg-gradient-to-b'
</script>

<template>
  <main v-if="managedProjects" class="flex w-full flex-col overflow-hidden">
    <Container
      class="screen-content relative flex flex-col items-center space-y-6 pb-16 pt-32 md:pb-16 lg:pb-16 lg:pt-48"
    >
      <div class="mt-16 flex w-full flex-1 items-center justify-between">
        <div>
          <ReviewsSmall />

          <Heading size="display" as="h1" class="w-full text-center lg:text-left">
            <span :class="cx(gradient, 'from-gray-950 to-gray-600 pr-2 text-5xl lg:text-7xl')">
              {{ $t('managed_projects.title.top') }}
            </span>
            <br />
          </Heading>

          <Paragraph
            v-if="managedProjects.hero?.body"
            class="mt-4 w-full text-balance pb-8 text-center text-gray-600 lg:w-2/3 lg:text-left"
            size="medium"
            align="left"
          >
            {{ managedProjects.hero.body }}
          </Paragraph>

          <Action as="link" to="/contact" intent="main" size="lg">
            {{ $t('managed_projects.cta', 'Contact Us') }}
          </Action>
        </div>

        <Menu class="hidden md:block" @update-tab="onTabChange" :title="managedProjects.menu?.title ?? undefined">
          <li
            class="text-md mb-4 flex list-none items-center gap-2 space-y-2 rounded-md border border-gray-300 p-3 text-gray-600"
            v-for="option in currentTab === 'Startup'
              ? managedProjects.menu?.startupOptions
              : managedProjects.menu?.options"
          >
            <CheckGreen class="h-6 w-6" />
            {{ typeof option === 'string' ? option : option.title }}
          </li>
        </Menu>
      </div>

      <span class="pt-24 text-gray-400">{{ $t('managed_projects.heroParagraph') }}</span>

      <Partners
        :title="false"
        :partners="managedProjects.testimonials?.partners ?? []"
        class="mt-4 px-8 pb-14 lg:px-0"
      />

      <div class="pointer-events-auto absolute inset-0 -z-10 h-full w-full px-4 md:px-8">
        <PixelGrid class="h-full" :pattern="pixelGridLg" />
      </div>
    </Container>

    <Container
      :guides="false"
      v-if="managedProjects.info"
      class="screen-content flex flex-col items-center space-y-6 overflow-hidden bg-white pb-4"
    >
      <ShadowBorder class="pb-16" position="top" />

      <Heading as="h2" size="caption">{{ managedProjects.info.eyebrow }}</Heading>
      <Heading as="h3" size="medium" class="text-center md:text-left">{{ managedProjects.info.title }}</Heading>

      <div
        v-if="managedProjects.info.infoCards"
        class="grid-col-1 grid w-full gap-16 text-center md:pb-16 lg:grid-cols-3 lg:pb-16 lg:pt-24"
      >
        <div v-for="infoCard in managedProjects.info.infoCards" :key="infoCard.title">
          <Heading class="bg-gradient-to-b from-main-950 to-gray-50 bg-clip-text text-transparent">
            {{ infoCard.title }}
          </Heading>
          <Paragraph class="mt-8 text-gray-600">{{ infoCard.description }}</Paragraph>
        </div>
      </div>

      <Action intent="main" as="link" to="/contact">{{ $t('managed_projects.ctaLabel') }}</Action>
    </Container>

    <Container
      :guides="false"
      v-if="managedProjects.info?.stats"
      class="screen-content relative mt-24 grid grid-cols-2 items-center justify-center gap-8 md:flex"
    >
      <div
        v-for="stats of managedProjects.info.stats"
        :key="stats.title"
        class="relative mb-8 text-center md:text-left"
      >
        <Paragraph as="h2" size="small" class="!text-gray-500 md:!text-left">{{ stats.title }}</Paragraph>
        <Heading as="p" size="medium">{{ stats.data }}</Heading>
      </div>

      <div class="pointer-events-none absolute mx-auto h-full w-full max-w-xl">
        <div
          class="absolute bottom-0 left-0 h-full w-full origin-bottom scale-[3] bg-radial-gradient-main md:scale-100 md:scale-x-[1.75]"
        />
        <div class="absolute bottom-0 left-0 h-px w-full scale-x-150 bg-gradient-main md:scale-x-100" />
      </div>
    </Container>

    <SingleTestimonial
      v-if="managedProjects.singleTestimonial"
      class="border-b-0 py-14 lg:py-24"
      :data="managedProjects.singleTestimonial"
    />

    <Container class="screen-content relative px-2 py-8 lg:py-16">
      <div class="text-center">
        <Heading size="caption" as="h2">{{ managedProjects.benefitsSection?.eyebrow }}</Heading>
        <Heading size="large" as="h3">{{ managedProjects.benefitsSection?.title }}</Heading>
      </div>

      <div class="mt-16 sm:columns-2 md:columns-3">
        <BenefitCard
          v-for="benefit in managedProjects.benefitsSection?.benefits"
          :key="benefit.title"
          class="text-left! mb-16 w-full !break-inside-avoid"
          :benefit="benefit"
        />
      </div>

      <div class="pointer-events-auto absolute inset-0 -z-10 h-full w-full px-4 md:px-8">
        <PixelGrid class="h-full" :pattern="benefitsPixelGrid" />
      </div>
    </Container>

    <LazyTechStack class="py-48" :data="managedProjects.tech || undefined" />

    <Container v-if="managedProjects.howWeWork" class="screen-content px-8 py-16">
      <div class="mx-auto mb-6 flex w-full flex-col items-center justify-center text-center lg:w-1/2">
        <Heading as="h2" size="caption">{{ managedProjects.howWeWork.eyebrow }}</Heading>
        <Heading as="h3" size="large" class="pb-8">{{ managedProjects.howWeWork.title }}</Heading>
        <RichText :raw="managedProjects.howWeWork.body" />
      </div>

      <div v-for="card in managedProjects.howWeWork.infoCards" :key="card.number ?? undefined">
        <div class="relative flex items-center justify-center">
          <div
            v-if="card.video"
            class="absolute left-0 right-0 top-10 h-[0.2px] -translate-x-0 rotate-[-2deg] bg-gray-200"
          ></div>
          <HowWeWorkCard :card="card" />
        </div>
      </div>

      <div class="mb-2 mt-14 flex w-full items-center justify-center">
        <Action intent="main" as="link" to="/contact">{{ $t('managed_projects.ctaLabel') }}</Action>
      </div>
    </Container>

    <SingleTestimonial
      v-if="managedProjects.secondTestimonial"
      class="border-b-0 py-14 lg:py-24"
      :data="managedProjects.secondTestimonial"
    />

    <Container class="screen-content flex flex-col items-center px-2 py-6">
      <Testimonials class="w-full" :testimonials="managedProjects.testimonials?.testimonials ?? []" />
    </Container>

    <Container
      v-if="managedProjects.faq"
      class="screen-content flex flex-col items-center pb-16 pt-32 text-center md:pb-16 lg:pb-16 lg:pt-24"
    >
      <Heading size="caption" as="h2">{{ managedProjects.faq.headline }}</Heading>
      <Heading as="h3" size="large" class="w-full lg:w-1/2">{{ managedProjects.faq.title }}</Heading>
      <Paragraph class="mt-2 w-full pb-6 lg:w-1/2">{{ managedProjects.faq.body }}</Paragraph>

      <Accordion
        v-if="managedProjects.faq.questions"
        class="mt-8 pb-16 text-sm xs:px-12 xl:px-72"
        :items="managedProjects.faq.questions"
      />
    </Container>

    <BlogSection v-if="managedProjects.blog" :data="managedProjects.blog" />
    <OtherSection v-if="managedProjects.otherServices" :data="managedProjects.otherServices" />
    <LazyCta class="pt-24" :href="lp('/contact')" :label="$t('home.contact.ctaLabel')" />
  </main>
</template>
